import React, { useState, useEffect } from 'react';
import { getOptimizedImageUrl } from '../../utils/image';

interface AvatarProps {
  src?: string | null;
  alt: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full';
  className?: string;
  onClick?: () => void;
  shape?: 'circle' | 'rounded' | 'square';
  isDeleted?: boolean;
}

const Avatar: React.FC<AvatarProps> = ({ 
  src, 
  alt, 
  size = 'md', 
  className = '',
  onClick,
  shape = 'circle',
  isDeleted = false
}) => {
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [src]);

  const getInitials = (name: string) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const sizeClasses = {
    xs: 'h-6 w-6 text-xs',
    sm: 'h-8 w-8 text-xs',
    md: 'h-10 w-10 text-sm',
    lg: 'h-12 w-12 text-base',
    xl: 'h-16 w-16 text-lg',
    '2xl': 'h-24 w-24 text-xl',
    '3xl': 'h-32 w-32 text-2xl',
    '4xl': 'h-40 w-40 text-3xl',
    'full': 'w-full h-full text-4xl'
  };

  const shapeClasses = {
    circle: 'rounded-full',
    rounded: 'rounded-xl',
    square: 'rounded-none'
  };

  const baseClasses = `${shapeClasses[shape]} object-cover flex-shrink-0 ${sizeClasses[size]} ${className}`;

  if (isDeleted) {
    return (
      <div 
        className={`${baseClasses} bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center text-[5px] border-2 border-white dark:border-gray-900 shadow-sm`}
        onClick={onClick}
        title="Deleted User"
      >
        Deleted
      </div>
    );
  }

  if (!src || imageError) {
    return (
      <div 
        className={`${baseClasses} bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 flex items-center justify-center font-medium border-2 border-white dark:border-gray-900 shadow-sm`}
        onClick={onClick}
        title={alt}
      >
        {getInitials(alt)}
      </div>
    );
  }

  // Determine optimal image size
  const optimalSize = ['2xl', '3xl', '4xl', 'full'].includes(size) ? 'medium' : 'thumbnail';
  const optimizedSrc = getOptimizedImageUrl(src, optimalSize);

  return (
    <img
      src={optimizedSrc}
      alt={alt}
      className={baseClasses}
      onError={() => setImageError(true)}
      onClick={onClick}
    />
  );
};

export default Avatar;
